/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useAuth0} from '@auth0/auth0-react';
import PersonIcon from '@mui/icons-material/Person';
import {Alert, AlertTitle, Box, Button, Stack, Typography} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import type {FeatureFlagSet} from '../flags';
import {getValidLandingRoute, ROUTES} from '../routes';
import {useUserStore} from '../stores/userStore';

const HomePage: React.FC = () => {
  const {loginWithRedirect, isAuthenticated} = useAuth0();
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const location = useLocation();
  const flags = useFlags<FeatureFlagSet>();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasPermissions = Object.keys(permissionsMap).length > 0;

  const handleLoginClick = () => {
    void loginWithRedirect();
  };

  useEffect(() => {
    if (isAuthenticated && hasPermissions) {
      const validLandingRoute = getValidLandingRoute(flags);
      const nextRoute = validLandingRoute || ROUTES.DEFAULT;
      navigate(nextRoute);
    }
  }, [isAuthenticated, hasPermissions]);

  useEffect(() => {
    const authErrorMessage = localStorage.getItem('TRUSST_AUTH_ERROR');
    if (authErrorMessage) {
      setErrorMessage(authErrorMessage);
      localStorage.removeItem('TRUSST_AUTH_ERROR');
    }
  }, []);

  useEffect(() => {
    if (location.state?.error) {
      setErrorMessage(location.state.error);
    }
  }, [location]);

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle, rgba(255,105,180,0.1) 0%, rgba(0,0,0,0) 70%)',
          zIndex: -1,
        }}
      />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          p: 2,
          paddingBottom: '10vh',
          width: '100vw',
        }}
      >
        <Stack spacing={6} maxWidth={'sm'}>
          <Box
            component={'img'}
            src={'/images/trusstai-logo-dark.png'}
            alt={'Trusst AI Logo'}
            sx={{maxWidth: '220px', mx: 'auto'}}
          />
          <Stack spacing={1}>
            <Typography variant={'h3'} color={'primary'} textAlign={'center'}>
              Empowering the Intelligent Workforce
            </Typography>
            <Typography variant={'h6'} textAlign={'center'}>
              Insights and Automation to Transform Customer Engagement
            </Typography>
          </Stack>

          {errorMessage && (
            <Alert variant={'outlined'} severity={'error'}>
              <AlertTitle>Authentication Error</AlertTitle>
              <Box>{errorMessage}</Box>
            </Alert>
          )}

          <Stack direction={'row'} spacing={2} justifyContent={'center'}>
            <Button
              variant={'contained'}
              color={'primary'}
              size={'large'}
              onClick={handleLoginClick}
              startIcon={<PersonIcon />}
            >
              Sign In
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default HomePage;
